import { captureAndUploadSlideScreenshot } from "./slideScreenshot";
import {
  showDynamicTutorViewNotification,
  NotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";
import {
  NEXT_BITE_TOAST,
  NOTIFICATION_STACKS,
  NotificationStackId,
} from "@/constants/notification";
import { handleAssessmentBeforeSlide } from "./assessmentHelpers";
import { handleLessonEnd } from "./endLesson";
import { ASSESSMENT_KEYS } from "../constants/assessmentTypes";
import {
  SLIDE_CHANGE_LOCK_MAX_MS,
  useSlideStore,
} from "@/features/tutor/store/useSlideStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { fireBoxBurst } from "./celebrationConfetti";
import { playCelebrationSound } from "./celebrationSounds";
import { persistNotepadCheckpoint } from "@/features/notepad/components/canvasApi";

// Long enough for a frame or two on a slow device, short enough that a hidden
// tab (no frames at all) still moves on well inside the countdown.
const PAINT_FALLBACK_DELAY_MS = 500;

/**
 * Resolves once the next frame has been painted — or after the fallback delay,
 * since a hidden tab paints none.
 */
function afterNextPaint() {
  return new Promise((resolve) => {
    const fallbackId = setTimeout(resolve, PAINT_FALLBACK_DELAY_MS);
    requestAnimationFrame(() =>
      setTimeout(() => {
        clearTimeout(fallbackId);
        resolve();
      }, 0),
    );
  });
}

/** Resolves when the sound and confetti have started. */
export function celebrateBiteCompletion() {
  // Double-rAF so the "Next bite" toast has rendered and fireBoxBurst can read
  // its on-screen position before popping confetti out of it. The sound starts
  // in the same callback so the two land together even when long tasks delay
  // the frames.
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        playCelebrationSound("bite");
        fireBoxBurst();
        resolve();
      });
    });
  });
}

/**
 * Starts the capture once toast, sound and confetti are on screen: they run on
 * the compositor, audio thread and a worker, so they carry on through the
 * clone's freeze. A timer covers a hidden tab (no frames); a moved-on index
 * skips the capture, since the DOM may no longer be that slide.
 */
function captureAfterCelebration(celebrationStarted, slideIndex) {
  return new Promise((resolve) => {
    let fallbackId = null;
    let isStarted = false;
    const startCapture = () => {
      if (isStarted) return;
      isStarted = true;
      clearTimeout(fallbackId);
      if (useSlideStore.getState().currentSlideIndex !== slideIndex) {
        resolve();
        return;
      }
      captureAndUploadSlideScreenshot(slideIndex).then(resolve, resolve);
    };
    fallbackId = setTimeout(startCapture, PAINT_FALLBACK_DELAY_MS);
    celebrationStarted.then(afterNextPaint).then(startCapture);
  });
}

export async function executeSlideTransition({
  emit,
  navigate,
  searchParams,
  setSearchParams,
  setActiveAssessmentModal,
  persistSlideIndexInUrl = false,
}) {
  emit("LOCAL_INTERRUPT");

  const currentSlideIndex = useSlideStore.getState().currentSlideIndex;
  const slides = useSlideStore.getState().slides ?? [];
  const sessionId = useSessionStore.getState().sessionId;
  const handleChangeSlide = useSlideStore.getState().handleChangeSlide;
  if (!sessionId) return;

  // Snapshot: a Next Bite click can advance the index during the countdown and
  // the awaits below, and a target resolved at call time would then skip a bite.
  const targetSlideIndex = currentSlideIndex + 1;
  const nextSlide = slides[targetSlideIndex];
  const nextSlideType =
    nextSlide?.metadata?.slide_type ?? nextSlide?.slide_type;

  if (!nextSlide) {
    await handleLessonEnd(navigate, searchParams);
    return;
  }

  if (ASSESSMENT_KEYS.includes(nextSlideType)) {
    // This return leaves before the checkpoint below, and the modal's own
    // buttons (VoDemo) then navigate off the CURRENT slide — which may be a
    // notepad bite. Its tldraw snapshot is client-owned and has no other
    // delivery path, so checkpoint here too. No-op on non-notepad slides.
    // (The !nextSlide branch above needs none: handleLessonEnd persists.)
    await persistNotepadCheckpoint();
    await handleAssessmentBeforeSlide(
      nextSlideType,
      setActiveAssessmentModal,
      0,
      {
        navigate,
        searchParams,
      },
    );
    return;
  }

  // Our own hold, independent of the toast's: the toast releases when its 3s
  // are up, but the navigation is still two awaits and a request away.
  const { acquireSlideChangeLock, releaseSlideChangeLock } =
    useNotificationStore.getState();
  acquireSlideChangeLock();
  let lockTimeoutId;

  try {
    const notificationPromise = showDynamicTutorViewNotification(
      NEXT_BITE_TOAST.titleTemplate,
      {},
      NEXT_BITE_TOAST.subheading,
      NotificationType.NEXT_BITE,
      NEXT_BITE_TOAST.durationMs,
      NOTIFICATION_STACKS[NotificationStackId.NEXT_SLIDE],
    );

    // Rasterise while the countdown runs, once the celebration is up. It has to
    // finish before the DOM changes, but awaiting it after the countdown left
    // the buttons locked with the toast gone and nothing explaining the wait.
    const screenshotDone = captureAfterCelebration(
      celebrateBiteCompletion(),
      currentSlideIndex,
    );

    const completed = await notificationPromise;
    if (!completed) return;

    await screenshotDone;
    // Synchronous canvas-history checkpoint (practice notepad bites) BEFORE
    // change_slide — the backend snapshot reads Redis when handling it.
    // Bounded (2s) and non-fatal: never blocks navigation.
    await persistNotepadCheckpoint();
    // The toast's exit animation starts as it is removed; let one frame hand it
    // to the compositor, or the next slide's mount freezes it on screen.
    await afterNextPaint();
    const slideChange = handleChangeSlide(sessionId, targetSlideIndex, "ai");
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("sessionId", sessionId);
        // Preview URLs keep slideIndex so refresh/deep links reopen the same slide.
        // Public sessions use runtime state; a stale URL slideIndex can override restart/resume boot.
        if (persistSlideIndexInUrl) {
          newParams.set("slideIndex", targetSlideIndex.toString());
        } else {
          newParams.delete("slideIndex");
        }
        return newParams;
      },
      { replace: true },
    );
    // Hold until change_slide answers: the index has already moved, and Previous
    // or the bite list would otherwise supersede the request.
    await Promise.race([
      slideChange,
      new Promise((resolve) => {
        lockTimeoutId = setTimeout(resolve, SLIDE_CHANGE_LOCK_MAX_MS);
      }),
    ]);
  } finally {
    clearTimeout(lockTimeoutId);
    releaseSlideChangeLock();
  }
}
